import { useState } from "react";
import { Alert, Box } from "@mui/material";
import Typography from "components/basic/typography";
import {
  useAnalyticsBreakdownsQuery,
  useAnalyticsFunnelQuery,
  useAnalyticsOverviewQuery,
  useAnalyticsTimeseriesQuery,
  useMarketplaceHealthQuery,
} from "generated/graphql";
import AnalyticsFilters from "./components/AnalyticsFilters";
import BreakdownTable from "./components/BreakdownTable";
import EstimatorFunnel from "./components/EstimatorFunnel";
import MarketplaceHealth from "./components/MarketplaceHealth";
import StatTile from "./components/StatTile";
import TrafficChart from "./components/TrafficChart";
import { formatNumber, formatPercent } from "./helpers/format";

/**
 * First-party analytics, visible only to accounts in ANALYTICS_ADMIN_EMAILS.
 *
 * State owner for the two filters; every panel below is presentational.
 */
const Analytics: React.FC = () => {
  const [days, setDays] = useState(30);
  const [site, setSite] = useState<string | null>(null);

  const variables = { days, site };

  const [overview] = useAnalyticsOverviewQuery({ variables });
  const [timeseries] = useAnalyticsTimeseriesQuery({ variables });
  const [breakdowns] = useAnalyticsBreakdownsQuery({ variables });
  const [funnel] = useAnalyticsFunnelQuery({ variables: { days } });
  const [health] = useMarketplaceHealthQuery({ variables: { days } });

  // One rejected query means the session is not an allowlisted admin; the
  // others will have failed the same way, so show it once.
  const error = overview.error ?? timeseries.error ?? breakdowns.error;
  const stats = overview.data?.analyticsOverview;
  const rows = breakdowns.data?.analyticsBreakdowns;

  if (error) {
    return (
      <Box sx={{ padding: 3 }}>
        <Alert severity="error">
          {error.message.includes("Not authorized") ||
          error.message.includes("Not authenticated")
            ? "This dashboard is limited to analytics administrators."
            : error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        padding: { xs: 2, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: 3,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography variant="h5" style={{ fontWeight: 700 }}>
          Analytics
        </Typography>

        <AnalyticsFilters
          days={days}
          site={site}
          onDaysChange={setDays}
          onSiteChange={setSite}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(5, 1fr)",
          },
        }}
      >
        <StatTile
          label="Unique visitors"
          value={formatNumber(stats?.uniqueVisitors ?? 0)}
          hint={
            days > 1
              ? "Tracking stores no cookie or device id, so someone visiting on several days counts once per day. Over a multi-day range this is an upper bound; the daily figures in the chart are exact."
              : undefined
          }
        />
        <StatTile
          label="Page views"
          value={formatNumber(stats?.pageviews ?? 0)}
        />
        <StatTile
          label="Estimator starts"
          value={formatNumber(stats?.estimatorStarts ?? 0)}
        />
        <StatTile
          label="Estimator submissions"
          value={formatNumber(stats?.estimatorSubmissions ?? 0)}
          detail={`${formatPercent(
            stats?.estimatorSubmissions ?? 0,
            stats?.estimatorStarts ?? 0,
          )} of starts`}
        />
        <StatTile
          label="Client errors"
          value={formatNumber(stats?.errors ?? 0)}
          hint="Counts and trends only — this is not a substitute for a stack-trace error tracker."
        />
      </Box>

      <TrafficChart
        points={timeseries.data?.analyticsTimeseries ?? []}
        isLoading={timeseries.fetching}
      />

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", lg: "repeat(3, 1fr)" },
        }}
      >
        <BreakdownTable
          title="Provinces & states"
          rows={rows?.regions ?? []}
          isLoading={breakdowns.fetching}
        />
        <BreakdownTable
          title="Cities"
          rows={rows?.cities ?? []}
          isLoading={breakdowns.fetching}
        />
        <BreakdownTable
          title="Countries"
          rows={rows?.countries ?? []}
          isLoading={breakdowns.fetching}
        />
      </Box>

      <EstimatorFunnel
        steps={funnel.data?.analyticsFunnel ?? []}
        isLoading={funnel.fetching}
      />

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(2, 1fr)",
            lg: "repeat(4, 1fr)",
          },
        }}
      >
        <BreakdownTable
          title="Top pages"
          rows={rows?.pages ?? []}
          isLoading={breakdowns.fetching}
        />
        <BreakdownTable
          title="Referrers"
          rows={rows?.referrers ?? []}
          isLoading={breakdowns.fetching}
        />
        <BreakdownTable
          title="Devices"
          rows={rows?.devices ?? []}
          isLoading={breakdowns.fetching}
        />
        <BreakdownTable
          title="Browsers"
          rows={rows?.browsers ?? []}
          isLoading={breakdowns.fetching}
        />
      </Box>

      <MarketplaceHealth health={health.data?.marketplaceHealth} />
    </Box>
  );
};

export default Analytics;
